import { useCallback, useRef, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import {
  type TransferServiceApi,
  type V1CreateWithdrawalSummaryRequest,
  type V1CreateWithdrawalSummaryResponse,
} from '@fintronners/react-api/src';

type UseWithdrawalSummaryOptions = {
  apiService: TransferServiceApi;
  withDebounce?: boolean;
  shouldSkip?: boolean;
  onRequestStart?: () => void;
  onRequestEnd?: () => void;
  onSuccess?: (data: V1CreateWithdrawalSummaryResponse) => void;
  onError?: (error: Error) => void;
};

const DEFAULT_DEBOUNCE_TIME = 1000;

/**
 * Hook for fetching the distribution type for a withdrawal. Specifically useful for retirement accounts.
 */
const useWithdrawalSummary = ({
  apiService,
  withDebounce = false,
  shouldSkip = false,
  onRequestStart,
  onRequestEnd,
  onSuccess,
  onError,
}: UseWithdrawalSummaryOptions) => {
  const transferApiService = apiService;
  const abortControllerRef = useRef<AbortController | null>(null);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onRequestStartRef = useRef(onRequestStart);
  const onRequestEndRef = useRef(onRequestEnd);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onRequestStartRef.current = onRequestStart;
  }, [onRequestStart]);

  useEffect(() => {
    onRequestEndRef.current = onRequestEnd;
  }, [onRequestEnd]);

  const getWithdrawalSummary = useCallback(
    async (options: V1CreateWithdrawalSummaryRequest) => {
      if (shouldSkip) {
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      onRequestStartRef.current?.();

      try {
        const { data: response } = await transferApiService.transferServiceCreateWithdrawalSummary(
          options,
          {
            signal: abortControllerRef.current.signal,
          },
        );

        onSuccessRef.current?.(response);

        return response;
      } catch (error) {
        if (
          error instanceof Error &&
          (error.name === 'CanceledError' || error.name === 'AbortError')
        ) {
          return;
        }

        const errorToSet = error instanceof Error ? error : new Error('Unknown error occurred');

        onErrorRef.current?.(errorToSet);

        return undefined;
      } finally {
        onRequestEndRef.current?.();
      }
    },
    [transferApiService, shouldSkip],
  );

  const debouncedGetWithdrawalSummary = useMemo(
    () =>
      debounce(getWithdrawalSummary, DEFAULT_DEBOUNCE_TIME, {
        leading: !withDebounce,
        trailing: withDebounce,
      }),
    [getWithdrawalSummary, withDebounce],
  );

  return {
    getWithdrawalSummary: debouncedGetWithdrawalSummary,
  };
};

export default useWithdrawalSummary;
